/**
 * TanStack Query client configuration.
 *
 * Default query options:
 *   - staleTime: 30s  — data is fresh for 30 seconds after fetch
 *   - gcTime: 5min    — unused cache entries are garbage-collected after 5 min
 *   - retry: 2        — retry failed queries twice before showing error
 *   - refetchOnWindowFocus: false — no automatic refetch on tab focus
 *   - refetchOnReconnect: true    — refetch when network reconnects
 *
 * Default mutation options:
 *   - retry: 0        — mutations are not retried on failure
 *
 * Global error handling:
 *   - 401 Unauthorized → calls auth store logout() + redirects to /login
 *   - 429 Too Many Requests → shows rate-limit toast notification
 *   - 5xx Server Error → shows generic error toast
 *
 * Usage:
 *   Wrap your app in <QueryClientProvider client={queryClient}>
 *   Then use useQuery(), useMutation(), etc. in components.
 *
 * Example:
 *   const { data, isLoading } = useQuery({
 *     queryKey: ['documents', orgId],
 *     queryFn: () => api.documents.list(orgId),
 *   });
 */

import { QueryClient } from '@tanstack/react-query';
import { toast } from './queryClient.toast'; // Re-exported below to avoid circular import

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ApiError {
  status?: number;
  message?: string;
  code?: string;
}

// ---------------------------------------------------------------------------
// Error classification helpers
// ---------------------------------------------------------------------------

function getStatusFromError(error: unknown): number | null {
  if (typeof error === 'object' && error !== null) {
    const e = error as Record<string, unknown>;
    if (typeof e['status'] === 'number') return e['status'];
    if (typeof e['statusCode'] === 'number') return e['statusCode'];
    // Axios-style error
    const response = e['response'] as Record<string, unknown> | undefined;
    if (response && typeof response['status'] === 'number') return response['status'];
  }
  return null;
}

// ---------------------------------------------------------------------------
// Global query error handler
// ---------------------------------------------------------------------------

function handleQueryError(error: unknown): void {
  const status = getStatusFromError(error);
  const apiError = error as ApiError;
  const message = apiError?.message ?? 'An unexpected error occurred';

  // 401 is handled by the axios interceptor (refresh + redirect). Don't fight it here.
  if (status === 401 || status === 403) return;

  if (status === 429) {
    toast.warning('Rate limit reached — please slow down', {
      description: 'Too many requests. Wait a moment before trying again.',
      duration: 8000,
    });
    return;
  }

  if (status && status >= 500) {
    toast.error('Server error', {
      description: message,
    });
    return;
  }
}

// ---------------------------------------------------------------------------
// QueryClient singleton
// ---------------------------------------------------------------------------

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,        // 30 seconds
      gcTime: 5 * 60_000,       // 5 minutes
      retry: (failureCount, error) => {
        const status = getStatusFromError(error);
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
      onError: handleQueryError,
    },
  },
});

// Override the global query error event handler
queryClient.getQueryCache().config.onError = handleQueryError;

// ---------------------------------------------------------------------------
// Toast import shim
// Imported here to decouple from the hook; the actual implementation is in useToast.ts
// ---------------------------------------------------------------------------

// We re-export from the hook module to keep one source of truth
export { toast } from './queryClient.toast';
